// Landing-page attribution for the SEO loop: which pages bring in sign-ups, trials and paid plans.
// The visitor's first-ever landing page and this session's landing page are stored on first load, then
// attached to conversion events so Amplitude can chart conversions by the SEO page that started the visit.

interface Touch {
  path: string;
  referrer: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  at: string;
}

const FIRST_TOUCH_KEY = 'kreatli_first_touch';
const SESSION_TOUCH_KEY = 'kreatli_session_touch';

// URL sections that hold SEO content, mapped to a short label for charts.
const CONTENT_SECTIONS: Record<string, string> = {
  '/free-tools': 'free-tool',
  '/safe-zone-checker': 'free-tool',
  '/sizes': 'sizes',
  '/templates': 'template',
  '/export-settings': 'export-settings',
  '/platform': 'platform',
  '/solutions': 'solution',
  '/comparisons': 'comparison',
  '/guides': 'guide',
  '/blog': 'blog',
  '/pricing': 'pricing',
};

const readTouch = (storage: Storage, key: string): Touch | null => {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as Touch) : null;
  } catch {
    return null;
  }
};

const writeTouch = (storage: Storage, key: string, touch: Touch) => {
  try {
    storage.setItem(key, JSON.stringify(touch));
  } catch {
    // Storage can be blocked (private mode, disabled cookies); attribution is best-effort.
  }
};

const externalReferrer = () => {
  if (!document.referrer) {
    return '';
  }

  try {
    const referrer = new URL(document.referrer);
    return referrer.host === window.location.host ? '' : referrer.host;
  } catch {
    return '';
  }
};

const currentTouch = (): Touch => {
  const params = new URLSearchParams(window.location.search);

  return {
    path: window.location.pathname,
    referrer: externalReferrer(),
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
    at: new Date().toISOString(),
  };
};

export const contentTypeForPath = (path: string) => {
  const section = Object.keys(CONTENT_SECTIONS).find((prefix) => path === prefix || path.startsWith(`${prefix}/`));

  if (section) {
    return CONTENT_SECTIONS[section];
  }

  return path === '/' ? 'home' : 'app';
};

const touchProperties = (prefix: string, touch: Touch | null): Record<string, string> => {
  if (!touch) {
    return {};
  }

  return {
    [`${prefix}_landing_path`]: touch.path,
    [`${prefix}_landing_type`]: contentTypeForPath(touch.path),
    [`${prefix}_referrer`]: touch.referrer || 'direct',
    ...(touch.utmSource && { [`${prefix}_utm_source`]: touch.utmSource }),
    ...(touch.utmMedium && { [`${prefix}_utm_medium`]: touch.utmMedium }),
    ...(touch.utmCampaign && { [`${prefix}_utm_campaign`]: touch.utmCampaign }),
  };
};

/** Records the first-ever and per-session landing page. Call once on app load, before any navigation. */
export const captureLandingPage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const touch = currentTouch();

  if (!readTouch(localStorage, FIRST_TOUCH_KEY)) {
    writeTouch(localStorage, FIRST_TOUCH_KEY, touch);
  }

  if (!readTouch(sessionStorage, SESSION_TOUCH_KEY)) {
    writeTouch(sessionStorage, SESSION_TOUCH_KEY, touch);
  }
};

/** First-touch and session-touch properties to attach to a conversion event. */
export const getLandingAttribution = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    ...touchProperties('first', readTouch(localStorage, FIRST_TOUCH_KEY)),
    ...touchProperties('session', readTouch(sessionStorage, SESSION_TOUCH_KEY)),
  };
};

/** First-touch properties only, for `setOnce` user properties. */
export const getFirstTouchProperties = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  return touchProperties('first', readTouch(localStorage, FIRST_TOUCH_KEY));
};
